import { Button, Flex, Text } from '@mantine/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dlpSettingsApiArea } from '@/services/api/dlpSettingsApiArea';
import { notifications } from '@mantine/notifications';
import classes from './styles.module.css';

interface DeleteDlpDialogProps {
  profileId: number;
  profileName: string;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onSuccess: () => Promise<void>;
}

const DeleteDlpDialog: React.FC<DeleteDlpDialogProps> = ({
  profileId,
  profileName,
  isOpen,
  setIsOpen,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dlpSettingsApiArea().DeleteDlpProfile(profileId);
      notifications.show({
        title: t('dlp.success'),
        message: t('dlp.deleteSuccess'),
        color: 'green'
      });
      await onSuccess();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to delete DLP profile:', error);
      notifications.show({
        title: t('dlp.error'),
        message: error instanceof Error ? error.message : t('dlp.deleteError'),
        color: 'red'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Text size="lg" fw={600}>
          {t('dlp.deleteIntegrationTitle')}
        </Text>
      </div>
      <div className={classes.body}>
        <Text size="sm">{t('dlp.deleteIntegrationConfirm')}</Text>
        <Text size="sm" fw={600} mt="sm">
          {profileName}
        </Text>
      </div>
      <Flex gap="sm" className={classes.footer}>
        <Button
          fullWidth
          color="red"
          onClick={handleDelete}
          loading={isDeleting}
          disabled={isDeleting}
        >
          {t('dlp.delete')}
        </Button>
        <Button
          fullWidth
          variant="light"
          color="gray"
          onClick={() => setIsOpen(false)}
          disabled={isDeleting}
        >
          {t('dlp.cancel')}
        </Button>
      </Flex>
    </div>
  );
};

export default DeleteDlpDialog;
